/**
 * Persistence for hand-dragged node positions.
 *
 * ## Why this is localStorage and not the service
 *
 * Positions are not part of the questionnaire record. `QuestionnaireNode` has no x/y,
 * and more to the point `SalesRecommendationHub` has NO write path for a definition at
 * all — the five `q_1_*.json` files are read off the Lambda's classpath. So nothing
 * about a draft persists server-side today, positions least of all.
 *
 * There IS a natural home for them when that API arrives: `QuestionnaireNode.metadata`
 * (`Map<String, Object>` on the bean, `Document` in Smithy) exists, is modelled on both
 * sides, and is unused in every live file — the engine never reads it. Layout belongs
 * there rather than in new top-level fields, because it is presentation data riding
 * along with the record, and putting it in `metadata` means the engine keeps ignoring
 * it by construction.
 *
 * Until then: per-browser, per-questionnaire, in localStorage. That covers what someone
 * actually wants from "save my shape" — survive a refresh and a tab switch — while
 * being honest that it is a view preference and not a change to the questionnaire. It
 * is deliberately NOT in the change list for the same reason.
 */
import type { PositionMap } from './draftReducer';

const PREFIX = 'cpa:layout:v1:';

/** Storage can be unavailable (private mode, blocked cookies); never let that throw. */
function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const keyFor = (definitionId: string) => `${PREFIX}${definitionId}`;

/**
 * Saved positions for a questionnaire, dropped to the node ids that still exist.
 *
 * Pruning on READ as well as on delete matters because the definition can change
 * without this surface knowing — a redeploy of the backend snapshot, or simply
 * switching questionnaires — and a position for a node that is gone would otherwise sit
 * in storage forever and could collide with a future node reusing the id.
 */
export function loadPositions(definitionId: string, liveNodeIds: readonly string[]): PositionMap {
  const s = store();
  if (!s) return {};
  try {
    const raw = s.getItem(keyFor(definitionId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const live = new Set(liveNodeIds);
    const out: PositionMap = {};
    for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (!live.has(id)) continue;
      const p = v as { x?: unknown; y?: unknown };
      if (typeof p?.x === 'number' && typeof p?.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)) {
        out[id] = { x: p.x, y: p.y };
      }
    }
    return out;
  } catch {
    // Corrupt entry: behave as if there were none rather than breaking the page.
    return {};
  }
}

export function savePositions(definitionId: string, positions: PositionMap): void {
  const s = store();
  if (!s) return;
  try {
    if (Object.keys(positions).length === 0) s.removeItem(keyFor(definitionId));
    else s.setItem(keyFor(definitionId), JSON.stringify(positions));
  } catch {
    // Quota or blocked storage. Losing a layout preference is not worth an error.
  }
}

export function clearPositions(definitionId: string): void {
  savePositions(definitionId, {});
}
